import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Standard "remembered tab" hook for section pages with top-level tabs.
 *
 * Drop-in replacement for `useState<Tab>(default)` that makes the active tab
 * survive navigation away and back, and be deep-linkable:
 *  - **Initial value** = URL `?<param>` (deep link / refresh wins) → else the
 *    last value stored in `sessionStorage` for this page → else `defaultTab`.
 *  - **On change** the value is written to BOTH `sessionStorage` (so returning
 *    to the page — even via a bare sidebar link with no query — restores the
 *    last tab) AND the URL `?<param>` with `replace` (so a refresh / shared
 *    link keeps the view). This is the generalisation of the per-page pattern
 *    that previously lived only in `CatalogPage`.
 *
 * @param storageKey  Stable per-page key (e.g. `'financial'`, `'inventory'`).
 * @param defaultTab  Fallback when nothing is stored / the stored value is stale.
 * @param validTabs   Optional whitelist — stored/URL values outside it are ignored.
 * @param paramName   URL query param name. Defaults to `'tab'`.
 *
 * `setTab(next, mutate)` writes the tab AND whatever `mutate` changes in ONE
 * navigation. Two navigations in one handler do not compose: each builds on
 * the search of the render it was created in, so the second drops what the
 * first set (that is how Financiero → «Ver los 13» lost `?mk=pending` and
 * opened «Todos»).
 */
export function useStickyTab<T extends string>(
  storageKey: string,
  defaultTab: T,
  validTabs?: readonly T[],
  paramName = 'tab',
): [T, (tab: T, mutate?: (params: URLSearchParams) => void) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionKey = `tab:${storageKey}`;

  const isValid = useCallback(
    (v: string | null | undefined): v is T =>
      !!v && (!validTabs || (validTabs as readonly string[]).includes(v)),
    [validTabs],
  );

  const [tab, setTabState] = useState<T>(() => {
    const fromUrl = searchParams.get(paramName);
    if (isValid(fromUrl)) return fromUrl;
    try {
      const stored = sessionStorage.getItem(sessionKey);
      if (isValid(stored)) return stored;
    } catch {
      /* sessionStorage unavailable (private mode / SSR) — fall through */
    }
    return defaultTab;
  });

  // Set by `setTab(next, mutate)`, which already wrote the URL: the effect
  // below must not write it again from this render's (older) search.
  const wroteUrl = useRef(false);
  const current = useRef(tab);
  current.current = tab;

  // Keep the URL reflecting the active tab (deep-link + refresh survive).
  useEffect(() => {
    if (wroteUrl.current) {
      wroteUrl.current = false;
      return;
    }
    if (searchParams.get(paramName) !== tab) {
      const next = new URLSearchParams(searchParams);
      next.set(paramName, tab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const setTab = useCallback(
    (next: T, mutate?: (params: URLSearchParams) => void) => {
      setTabState(next);
      try {
        sessionStorage.setItem(sessionKey, next);
      } catch {
        /* ignore — sessionStorage unavailable */
      }
      // Without `mutate` the effect above writes the URL, as it always has.
      if (!mutate) return;
      // Only a real change re-renders and runs the effect that clears it.
      wroteUrl.current = next !== current.current;
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.set(paramName, next);
        mutate(params);
        return params;
      }, { replace: true });
    },
    [sessionKey, paramName, setSearchParams],
  );

  return [tab, setTab];
}
